import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * ThemeContext — dark / light mode for the admin panel.
 *
 * The moon button switches to dark mode, the sun button back to light.
 * The choice persists in localStorage (`theme`) and defaults to the
 * device's own preference (prefers-color-scheme) on first visit.
 *
 * Dark mode works by toggling the `dark` class on <html>; index.css
 * remaps the panel's light utilities when that class is present.
 */
const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });

const THEME_KEY = 'panel_theme';

// Apply/remove the class + persist — safe to call before React mounts
const applyTheme = (theme) => {
  try {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
  } catch {
    // document not ready — ignore
  }
};

const readInitialTheme = () => {
  try {
    const stored = localStorage.getItem(THEME_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // localStorage unavailable — fall through to device preference
  }
  try {
    if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  } catch {
    // matchMedia unavailable — default light
  }
  return 'light';
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(readInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // localStorage unavailable — theme just won't persist
    }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;